import { getAllPatients } from '../constants/mockPatients';

const MIN_MATCH_SCORE = 45;
const CONFIDENT_SCORE = 72;
const SCORE_GAP = 12;

export function normalizeRoom(room) {
  if (room == null || room === '') return null;
  const text = String(room).trim().toLowerCase();
  const match = text.match(/(\d+[a-z]?)/i);
  return match ? match[1].toLowerCase() : null;
}

function normalizeName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .trim()
    .replace(/[.'’]/g, '')
    .replace(/\s+/g, ' ');
}

function nameTokens(name) {
  return normalizeName(name)
    .split(' ')
    .filter((token) => token.length >= 2);
}

function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const rows = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = rows[0];
    rows[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const temp = rows[j];
      rows[j] =
        a[i - 1] === b[j - 1]
          ? previous
          : 1 + Math.min(previous, rows[j], rows[j - 1]);
      previous = temp;
    }
  }
  return rows[b.length];
}

export function fuzzyNameSimilarity(a, b) {
  const left = normalizeName(a);
  const right = normalizeName(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.includes(right) || right.includes(left)) return 0.95;

  const distance = levenshteinDistance(left, right);
  const maxLen = Math.max(left.length, right.length);
  const minLen = Math.min(left.length, right.length);
  if (maxLen < 3) return 0;

  const ratio = 1 - distance / maxLen;
  if (minLen >= 3 && minLen <= 6 && distance <= 2 && left[0] === right[0]) {
    return Math.max(ratio, 0.82);
  }
  return ratio;
}

function getPatientNameVariants(patient) {
  return [patient.nameEn, patient.name].filter(Boolean);
}

function getFirstNameToken(patient) {
  for (const variant of getPatientNameVariants(patient)) {
    const token = nameTokens(variant)[0];
    if (token) return token;
  }
  return '';
}

function bestNameSimilarity(hint, patient) {
  if (!hint) return 0;
  let best = 0;
  for (const variant of getPatientNameVariants(patient)) {
    best = Math.max(best, fuzzyNameSimilarity(hint, variant));
    for (const token of nameTokens(variant)) {
      best = Math.max(best, fuzzyNameSimilarity(hint, token));
    }
  }
  return best;
}

function transcriptIncludesName(transcript, name) {
  if (!transcript || !name) return false;
  const normalizedTranscript = normalizeName(transcript);
  const fullName = normalizeName(name);
  if (fullName && normalizedTranscript.includes(fullName)) return true;

  const tokens = nameTokens(name);
  return tokens.some(
    (token) =>
      token.length >= 3 &&
      (normalizedTranscript.includes(token) || fuzzyNameSimilarity(token, normalizedTranscript) >= 0.82)
  );
}

function transcriptFuzzyIncludesPatient(transcript, patient) {
  if (!transcript) return 0;
  let best = 0;
  for (const variant of getPatientNameVariants(patient)) {
    if (transcriptIncludesName(transcript, variant)) {
      best = Math.max(best, 0.95);
    }
    for (const token of nameTokens(variant)) {
      if (token.length < 3) continue;
      const words = normalizeName(transcript).split(/\s+/);
      for (const word of words) {
        best = Math.max(best, fuzzyNameSimilarity(token, word));
      }
    }
  }
  return best;
}

export function extractRoomFromTranscript(transcript) {
  if (!transcript) return null;

  const patterns = [
    /\broom\s*#?\s*(\d+[a-z]?)\b/i,
    /\bbed\s*#?\s*(\d+[a-z]?)\b/i,
    /\bغرفة\s*(?:رقم\s*)?(\d+[a-z]?)\b/,
    /\bرقم\s*الغرفة\s*(\d+[a-z]?)\b/,
    /\bin\s+room\s+(\d+[a-z]?)\b/i,
  ];

  for (const pattern of patterns) {
    const match = transcript.match(pattern);
    if (match?.[1]) return match[1];
  }

  return null;
}

export function extractNameHintsFromTranscript(transcript) {
  if (!transcript) return [];

  const hints = new Set();
  const patterns = [
    /\bpatient\s+([a-z][a-z'-]{1,24}(?:\s+[a-z][a-z'-]{1,24})?)/gi,
    /\bfor\s+([a-z][a-z'-]{1,24}(?:\s+[a-z][a-z'-]{1,24})?)/gi,
    /\bmr\.?\s+([a-z][a-z'-]{1,24})/gi,
    /\bmrs\.?\s+([a-z][a-z'-]{1,24})/gi,
    /\bms\.?\s+([a-z][a-z'-]{1,24})/gi,
    /\bmiss\s+([a-z][a-z'-]{1,24})/gi,
  ];

  for (const pattern of patterns) {
    let match = pattern.exec(transcript);
    while (match) {
      if (match[1]) hints.add(match[1].trim());
      match = pattern.exec(transcript);
    }
  }

  return [...hints];
}

function collectNameHints(patientHints, transcript) {
  const hints = new Set();
  if (patientHints?.name) {
    hints.add(String(patientHints.name).trim());
    nameTokens(patientHints.name).forEach((token) => hints.add(token));
  }
  extractNameHintsFromTranscript(transcript).forEach((hint) => hints.add(hint));
  return [...hints].filter(Boolean);
}

function hasPatientIdentityHint(patientHints, transcript) {
  return collectNameHints(patientHints, transcript).length > 0;
}

function scorePatient(patient, { roomHint, nameHints, transcript }) {
  let score = 0;
  const reasons = [];
  const roomMatches = roomHint && normalizeRoom(patient.room) === roomHint;

  if (roomMatches) {
    score += 100;
    reasons.push('room');
  }

  let bestNameScore = 0;
  for (const hint of nameHints) {
    bestNameScore = Math.max(bestNameScore, bestNameSimilarity(hint, patient));
  }
  if (bestNameScore > 0) {
    score += bestNameScore * 80;
    reasons.push('name');
  }

  const transcriptScore = transcriptFuzzyIncludesPatient(transcript, patient);
  if (transcriptScore > 0) {
    score += transcriptScore * 40;
    reasons.push('transcript');
  }

  let matchType = 'fuzzy';
  if (roomMatches && bestNameScore >= 0.82) matchType = 'room_and_name';
  else if (roomMatches) matchType = 'room';
  else if (bestNameScore >= 0.82) matchType = 'name';
  else if (transcriptScore >= 0.82) matchType = 'transcript_name';

  return { patient, score, reasons, matchType, nameScore: bestNameScore };
}

export function rankPatientCandidates({ patients, transcript, patientHints }) {
  const roster = patients?.length ? patients : [];
  if (!roster.length) return [];

  const roomHint =
    normalizeRoom(patientHints?.room) || normalizeRoom(extractRoomFromTranscript(transcript));
  const nameHints = collectNameHints(patientHints, transcript);

  const scored = roster
    .map((patient) => scorePatient(patient, { roomHint, nameHints, transcript }))
    .filter((entry) => entry.score >= MIN_MATCH_SCORE)
    .sort((a, b) => b.score - a.score);

  if (roomHint) {
    const roomOnly = roster
      .filter((patient) => normalizeRoom(patient.room) === roomHint)
      .map((patient) => scorePatient(patient, { roomHint, nameHints, transcript }));
    if (roomOnly.length === 1) {
      const only = roomOnly[0];
      const existing = scored.find((entry) => entry.patient.id === only.patient.id);
      if (!existing || existing.score < only.score) {
        return [{ ...only, score: Math.max(only.score, CONFIDENT_SCORE + 10) }, ...scored.filter((entry) => entry.patient.id !== only.patient.id)];
      }
    }
  }

  return scored;
}

function shareAmbiguousFirstName(a, b) {
  const left = getFirstNameToken(a);
  const right = getFirstNameToken(b);
  if (!left || !right) return false;
  return left === right || fuzzyNameSimilarity(left, right) >= 0.9;
}

function hasDuplicateFirstNameAmongCandidates(candidates) {
  const groups = new Map();
  for (const entry of candidates) {
    const firstName = getFirstNameToken(entry.patient);
    if (!firstName) continue;
    groups.set(firstName, (groups.get(firstName) || 0) + 1);
  }
  return [...groups.values()].some((count) => count > 1);
}

function shouldAskUserToSelect(match) {
  return match.status !== 'resolved';
}

export function resolvePatientMatch({
  admittedPatients = [],
  patients = null,
  transcript,
  patientHints,
  patientId = null,
}) {
  if (patientId) {
    const roster = patients?.length ? patients : getAllPatients(admittedPatients);
    const patient = roster.find((entry) => entry.id === patientId);
    return patient
      ? { status: 'resolved', patient, candidates: [], matchType: 'patient_page' }
      : { status: 'unresolved', patient: null, candidates: [], matchType: null };
  }

  const roster = patients?.length ? patients : getAllPatients(admittedPatients);
  const candidates = rankPatientCandidates({ patients: roster, transcript, patientHints });

  if (!candidates.length) {
    return { status: 'unresolved', patient: null, candidates: [], matchType: null };
  }

  const [top, second] = candidates;

  if (top.score >= CONFIDENT_SCORE && (!second || top.score - second.score >= SCORE_GAP)) {
    if (!second || !shareAmbiguousFirstName(top.patient, second.patient)) {
      return {
        status: 'resolved',
        patient: top.patient,
        candidates,
        matchType: top.matchType,
      };
    }
  }

  if (candidates.length === 1 && top.score >= MIN_MATCH_SCORE) {
    if (top.nameScore >= 0.82 || top.reasons.includes('room')) {
      return {
        status: 'resolved',
        patient: top.patient,
        candidates,
        matchType: top.matchType,
      };
    }
  }

  if (
    second &&
    (top.score - second.score < SCORE_GAP ||
      shareAmbiguousFirstName(top.patient, second.patient) ||
      hasDuplicateFirstNameAmongCandidates(candidates))
  ) {
    return { status: 'ambiguous', patient: null, candidates, matchType: null };
  }

  if (top.score >= MIN_MATCH_SCORE) {
    return {
      status: 'resolved',
      patient: top.patient,
      candidates,
      matchType: top.matchType,
    };
  }

  return { status: 'unresolved', patient: null, candidates, matchType: null };
}

export function needsPatientSelection(match, patientHints, transcript) {
  return shouldAskUserToSelect(match);
}

export function resolvePatientFromHints({ patients, transcript, patientHints }) {
  const roster = patients?.length ? patients : [];
  if (!roster.length) return null;

  const match = resolvePatientMatch({ patients: roster, transcript, patientHints });
  if (match.status === 'resolved' && match.patient) {
    return { patient: match.patient, matchType: match.matchType };
  }

  return null;
}

export function resolvePatientForNote({
  admittedPatients = [],
  transcript,
  patientHints,
  patientId = null,
}) {
  const match = resolvePatientMatch({
    admittedPatients,
    transcript,
    patientHints,
    patientId,
  });

  if (match.status === 'resolved' && match.patient) {
    return { patient: match.patient, matchType: match.matchType };
  }

  return null;
}
